import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  VStack,
  HStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Badge,
  SimpleGrid,
  Icon,
  Flex,
  useColorModeValue
} from '@chakra-ui/react';
import { FiSearch, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import ReCAPTCHA from 'react-google-recaptcha';
import nominationService from '../services/api/nominationService';
import { getNominationStatusColor, getNominationStatusLabel, normalizeNominationStatus } from '../utils/evaluationStatusUtils';

const StatusCheck = () => {
  const [formData, setFormData] = useState({
    nominationId: '',
    nationalId: ''
  });
  const PREFIX = "MAJ-2025-";
  const [loading, setLoading] = useState(false);
  const [nominationStatus, setNominationStatus] = useState(null);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const toast = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear previous results when input changes
    if (nominationStatus) {
      setNominationStatus(null);
    }
    if (error) {
      setError('');
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      toast({
        title: 'خطأ في التحقق',
        description: 'يرجى التحقق من أنك لست روبوت',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Validate nomination ID format
    if (!formData.nominationId) {
      toast({
        title: 'رقم الطلب مطلوب',
        description: 'يرجى إدخال رقم الطلب',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Validate national ID format (should be 10 digits)
    if (!formData.nationalId) {
      toast({
        title: 'رقم الهوية مطلوب',
        description: 'يرجى إدخال رقم الهوية',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    } else if (!/^\d{10}$/.test(formData.nationalId)) {
      toast({
        title: 'صيغة رقم الهوية غير صحيحة',
        description: 'يجب أن يتكون رقم الهوية من 10 أرقام',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setError('');
    setNominationStatus(null);

    try {
      const response = await nominationService.checkNominationStatus(
        PREFIX + formData.nominationId,
        formData.nationalId
      );
      setNominationStatus(response);
      toast({
        title: 'تم بنجاح',
        description: 'تم العثور على الطلب بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error checking nomination status:', error);
      
      // Get the error message from the handled API error
      let errorMessage = error.message || 'حدث خطأ أثناء البحث عن الطلب. يرجى المحاولة مرة أخرى';
      const statusCode = error.statusCode || (error.response?.status || 500);
      
      // Provide more user-friendly error messages based on status code
      if (statusCode === 404) {
        errorMessage = 'لم يتم العثور على طلب ترشيح بهذا الرقم ورقم الهوية. يرجى التأكد من صحة البيانات المدخلة.';
      } else if (statusCode === 400) {
        errorMessage = 'البيانات المدخلة غير صحيحة. يرجى التأكد من صحة رقم الطلب ورقم الهوية.';
      } else if (statusCode === 403) {
        errorMessage = 'ليس لديك صلاحية للوصول إلى هذا الطلب.';
      } else if (statusCode === 429) {
        errorMessage = 'تم تجاوز عدد المحاولات المسموح بها. يرجى المحاولة مرة أخرى لاحقاً.';
      }
      
      // Set the error message to display in the UI
      setError(errorMessage);
      
      // Determine appropriate title based on status code
      let toastTitle = 'خطأ في النظام'; // Default: System Error
      
      if (statusCode === 404) {
        toastTitle = 'طلب غير موجود'; // Nomination not found
      } else if (statusCode === 400) {
        toastTitle = 'بيانات غير صحيحة'; // Invalid data
      } else if (statusCode === 403) {
        toastTitle = 'خطأ في الصلاحيات'; // Permission error
      } else if (statusCode === 429) {
        toastTitle = 'تجاوز عدد المحاولات'; // Too many attempts
      }
      
      // Show toast notification with the error
      toast({
        title: toastTitle,
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const norm = normalizeNominationStatus(status);
    const statusInfo = { colorScheme: getNominationStatusColor(norm), text: getNominationStatusLabel(norm) };
    
    return (
      <Badge colorScheme={statusInfo.colorScheme} fontSize="md" px={3} py={1}>
        {statusInfo.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Container maxW="container.lg" py={10}>
      <VStack spacing={8}>
        <Card bg={bgColor} shadow="lg" borderRadius="lg" w="full" maxW="2xl">
          <CardHeader color="white" textAlign="center" borderTopRadius="lg">
            <Heading size="lg">
              
              تتبع حالة طلب الترشيح
            </Heading>
          </CardHeader>
          <CardBody p={6}>
            <Text color="gray.600" textAlign="center" mb={6}>
              يمكنك الاستعلام عن حالة طلب الترشيح الخاص بك باستخدام رقم الطلب ورقم الهوية
            </Text>

            <Box as="form" onSubmit={handleSubmit}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                <FormControl isRequired>
                  <FormLabel>رقم الطلب</FormLabel>
                  <InputGroup dir="ltr">
                    <InputRightAddon children={PREFIX} bg="gray.100" color="gray.600" fontWeight="medium" />
                    <Input
                      type="text"
                      name="nominationId"
                      value={formData.nominationId}
                      onChange={handleInputChange}
                      placeholder="أدخل رقم الطلب"
                        sx={{
                        textAlign: 'left',
                        direction: 'ltr'
                      }}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>رقم الهوية</FormLabel>
                  <Input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    placeholder="أدخل رقم الهوية"
                    maxLength={10}
                  />
                </FormControl>
              </SimpleGrid>

              <Flex justify="center" mb={6}>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                  onChange={handleRecaptchaChange}
                  hl="ar"
                />
              </Flex>

              <Flex justify="center">
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  isDisabled={loading || !recaptchaToken}
                  isLoading={loading}
                  loadingText="جاري البحث..."
                  leftIcon={<Icon as={FiSearch} />}
                  px={8}
                >
                  البحث عن الطلب
                </Button>
              </Flex>
            </Box>

            {error && (
              <Alert status="error" mt={6} borderRadius="md" variant="left-accent">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">خطأ في البحث</Text>
                  <Text>{error}</Text>
                </Box>
              </Alert>
            )}

            {nominationStatus && (
              <Card mt={6} border="1px" borderColor="green.200" bg="green.50">
                <CardHeader bg="green.100" borderTopRadius="md">
                  <Heading size="md" color="green.700">
                    <Icon as={FiCheckCircle} mr={2} />
                    تم العثور على الطلب
                  </Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <VStack align="start" spacing={3}>
                      <Text><strong>رقم الطلب:</strong> {nominationStatus.id}</Text>
                      <Text><strong>اسم المتقدم:</strong> {nominationStatus.applicant_name || 'غير محدد'}</Text>
                      <HStack>
                        <Text><strong>الحالة:</strong></Text>
                        {getStatusBadge(nominationStatus.status)}
                      </HStack>
                      {/* Show status date if available */}
                      {nominationStatus.status_updated_at && (
                        <Text fontSize="sm" color="gray.600">
                          <strong>تاريخ تغيير الحالة:</strong> {formatDate(nominationStatus.status_updated_at)}
                        </Text>
                      )}
                    </VStack>
                    <VStack align="start" spacing={3}>
                      <Text><strong>الفرع:</strong> {nominationStatus.branch_name || 'غير محدد'}</Text>
                      <Text><strong>المركز:</strong> {nominationStatus.center_name || 'غير محدد'}</Text>
                      <Text><strong>تاريخ الإرسال:</strong> {formatDate(nominationStatus.submitted_at)}</Text>
                      {/* Show next steps based on status */}
                      {normalizeNominationStatus(nominationStatus.status) === 'submitted' && (
                        <Alert status="info" mt={3} borderRadius="md">
                          <AlertIcon />
                          طلبك قيد المراجعة حالياً من قبل اللجنة المختصة. سيتم إشعارك بأي تغيير في الحالة.
                        </Alert>
                      )}
                      {normalizeNominationStatus(nominationStatus.status) === 'draft' && (
                        <Text color="red.600" fontWeight="medium">
                          <Icon as={FiAlertCircle} mr={1} />
                          يرجى استكمال البيانات المطلوبة
                        </Text>
                      )}
                    </VStack>
                  </SimpleGrid>
                  <Box borderTop="1px" borderColor="gray.200" mt={4} pt={4}>
                    <Text fontSize="sm" color="gray.600">
                      <strong>آخر تحديث:</strong> {formatDate(nominationStatus.updated_at)}
                    </Text>
                    {/* Add status-specific messages */}
                    {nominationStatus.status === 'approved' && (
                      <Alert status="success" mt={3} borderRadius="md">
                        <AlertIcon />
                        تهانينا! تم قبول طلب الترشيح الخاص بك. سيتم التواصل معك قريباً لاستكمال الإجراءات.
                      </Alert>
                    )}
                  </Box>
                </CardBody>
              </Card>
            )}
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default StatusCheck;
